import { canSendObservability } from '../security/consentGuard';
import { sanitizeObject } from '../security/sensitiveDataSanitizer';
import type { ErrorSeverity, NormalizedErrorEvent } from '../types/error.types';
import type { ObservabilityApp } from '../types/tenant.types';
import { buildStableErrorHash } from '../utils/hash';
import { normalizeErrorEvent } from '../utils/normalizeError';
import { clampSampleRate, shouldSample } from '../utils/sampling';
import { nowIso } from '../utils/time';
import { addBreadcrumb as addTypedBreadcrumb, getBreadcrumbs } from './breadcrumbs';
import {
  clearUserContext,
  getFrontendContext,
  initFrontendContext,
  setUserContext as setFrontendUserContext,
  setTenantContext,
} from './frontendContext';
import { getRequestId } from './requestId';

export type ErrorType =
  | 'js_error'
  | 'api_error'
  | 'network_error'
  | 'performance_error'
  | 'ui_error';

export type LegacySeverity = 'low' | 'medium' | 'high' | 'critical';

export type BreadcrumbEntry = {
  action: string;
  route: string;
  createdAt: string;
  extra: Record<string, unknown>;
};

export type ClientErrorPayload = {
  type: ErrorType;
  severity: LegacySeverity;
  message: string;
  stack: string | null;
  route: string;
  pageTitle: string;
  appVersion: string;
  environment: string;
  userId: string | null;
  organizationId: string | null;
  role: string | null;
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  viewport: string;
  language: string;
  timezone: string;
  apiUrl: string | null;
  apiMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | null;
  statusCode: number | null;
  requestId: string | null;
  durationMs: number | null;
  breadcrumbs: BreadcrumbEntry[];
  extra: Record<string, unknown>;
  createdAt: string;
};

export type ApiErrorContext = {
  url?: string;
  method?: string;
  status?: number;
  requestId?: string;
  durationMs?: number;
  body?: unknown;
};

export type PerformanceMetric = {
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
};

type ReporterConfig = {
  endpoint: string;
  appVersion?: string;
  sampleRateMedium?: number;
  sampleRateLow?: number;
  sampleRate?: number;
  appName?: ObservabilityApp;
  service?: string;
  environment?: string;
  releaseId?: string;
  gitCommitSha?: string;
  deploymentId?: string;
  maxBatchSize?: number;
};

const queue: ClientErrorPayload[] = [];
const dedupTimestamps = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let endpoint = '';
let appVersion = 'unknown';
let sampleRateMedium = 0.5;
let sampleRateLow = 0.1;
let globalSampleRate = 1;
let isFlushing = false;
let retryCount = 0;
let maxBatchSize = 30;

const FLUSH_DELAY_MS = 300;
const DEDUP_WINDOW_MS = 30_000;
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 500;

function toLegacySeverity(severity: ErrorSeverity): LegacySeverity {
  switch (severity) {
    case 'info':
      return 'low';
    case 'warning':
      return 'medium';
    case 'error':
      return 'high';
    case 'critical':
    case 'fatal':
      return 'critical';
  }
}

function shouldCaptureSeverity(severity: LegacySeverity): boolean {
  const rate =
    severity === 'critical' || severity === 'high'
      ? globalSampleRate
      : severity === 'medium'
        ? clampSampleRate(sampleRateMedium * globalSampleRate, 0.5)
        : clampSampleRate(sampleRateLow * globalSampleRate, 0.1);
  return shouldSample(rate);
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushErrors();
  }, delay);
}

function mapContextToLegacy(normalized: NormalizedErrorEvent, type: ErrorType): ClientErrorPayload {
  const ctx = normalized.context;
  const isMobile = ctx.deviceType === 'mobile';
  const isTablet = ctx.deviceType === 'tablet';
  const deviceType: ClientErrorPayload['deviceType'] = isMobile
    ? 'mobile'
    : isTablet
      ? 'tablet'
      : 'desktop';

  return {
    type,
    severity: toLegacySeverity(normalized.severity),
    message: normalized.message,
    stack: normalized.stack ?? null,
    route: ctx.route ?? '',
    pageTitle: typeof document !== 'undefined' ? document.title : '',
    appVersion: ctx.version ?? appVersion,
    environment: ctx.environment,
    userId: ctx.userId ?? null,
    organizationId: ctx.tenantId ?? null,
    role: ctx.role ?? null,
    browser: ctx.browser ?? 'unknown',
    os: ctx.os ?? 'unknown',
    deviceType,
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    language: typeof navigator !== 'undefined' ? navigator.language : '',
    timezone: ctx.timezone ?? '',
    apiUrl: typeof normalized.extra?.apiUrl === 'string' ? normalized.extra.apiUrl : null,
    apiMethod:
      typeof normalized.extra?.apiMethod === 'string'
        ? (normalized.extra.apiMethod.toUpperCase() as ClientErrorPayload['apiMethod'])
        : null,
    statusCode:
      typeof normalized.context.statusCode === 'number' ? normalized.context.statusCode : null,
    requestId: ctx.requestId ?? getRequestId(),
    durationMs:
      typeof normalized.context.durationMs === 'number' ? normalized.context.durationMs : null,
    breadcrumbs: getBreadcrumbs().map((crumb) => ({
      action: crumb.type === 'user_action' ? crumb.message : crumb.type,
      route: crumb.route ?? '',
      createdAt: crumb.timestamp,
      extra: sanitizeObject({
        message: crumb.message,
        metadata: crumb.metadata,
      }),
    })),
    extra: sanitizeObject({
      ...(normalized.extra ?? {}),
      normalizedHash: normalized.hash,
      normalizedSeverity: normalized.severity,
      normalizedImpact: normalized.impact,
      releaseId: ctx.releaseId,
      gitCommitSha: ctx.gitCommitSha,
      deploymentId: ctx.deploymentId,
    }),
    createdAt: nowIso(),
  };
}

function enqueueNormalized(error: {
  source: 'frontend' | 'edge';
  category: NormalizedErrorEvent['category'];
  severity: ErrorSeverity;
  message: string;
  stack?: string;
  extra?: Record<string, unknown>;
  context?: Partial<NormalizedErrorEvent['context']>;
  type: ErrorType;
}): void {
  const normalized = normalizeErrorEvent({
    error: { message: error.message, stack: error.stack },
    source: error.source,
    category: error.category,
    severity: error.severity,
    context: getFrontendContext(error.context),
    breadcrumbs: getBreadcrumbs(),
    extra: error.extra,
  });

  const legacyPayload = mapContextToLegacy(normalized, error.type);
  if (!shouldCaptureSeverity(legacyPayload.severity)) return;
  if (!canSendObservability('analytics') && legacyPayload.severity !== 'critical') return;

  const dedupKey = buildStableErrorHash({
    message: legacyPayload.message,
    stack: legacyPayload.stack ?? undefined,
    source: 'frontend',
    category: normalized.category,
    route: legacyPayload.route,
    service: normalized.context.service,
    releaseId: normalized.context.releaseId,
  });
  const now = Date.now();
  const previous = dedupTimestamps.get(dedupKey);
  if (previous && now - previous < DEDUP_WINDOW_MS) return;
  dedupTimestamps.set(dedupKey, now);

  queue.push(legacyPayload);
  if (queue.length >= maxBatchSize) {
    scheduleFlush(0);
  } else {
    scheduleFlush();
  }
}

export function initErrorReporter(config: ReporterConfig): void {
  endpoint = config.endpoint;
  appVersion = config.appVersion ?? appVersion;
  sampleRateMedium = clampSampleRate(config.sampleRateMedium, sampleRateMedium);
  sampleRateLow = clampSampleRate(config.sampleRateLow, sampleRateLow);
  globalSampleRate = clampSampleRate(config.sampleRate, globalSampleRate);
  maxBatchSize = Math.max(1, config.maxBatchSize ?? maxBatchSize);
  queue.length = 0;
  dedupTimestamps.clear();
  retryCount = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  initFrontendContext({
    app: config.appName ?? 'tenant-app',
    service: config.service ?? 'frontend',
    environment: config.environment,
    version: config.appVersion,
    releaseId: config.releaseId,
    gitCommitSha: config.gitCommitSha,
    deploymentId: config.deploymentId,
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      void flushErrors();
    });
  }
}

export function setUserContextCompat(user: { userId: string; role?: string | null } | null): void {
  setFrontendUserContext(user);
}

export function setOrganizationContext(org: { organizationId: string } | null): void {
  setTenantContext(org ? { tenantId: org.organizationId } : null);
}

export function clearObservabilityUserContext(): void {
  clearUserContext();
}

export function addBreadcrumb(
  entry: { action: string; route?: string; createdAt?: string } & Record<string, unknown>,
): void {
  const action = String(entry.action);
  const typedAction =
    action === 'navigation' ||
    action === 'click' ||
    action === 'api' ||
    action === 'error' ||
    action === 'user_action' ||
    action === 'form' ||
    action === 'system'
      ? action
      : 'user_action';

  addTypedBreadcrumb({
    type: typedAction,
    message: action,
    route: typeof entry.route === 'string' ? entry.route : undefined,
    timestamp: typeof entry.createdAt === 'string' ? entry.createdAt : undefined,
    metadata: sanitizeObject(
      Object.fromEntries(
        Object.entries(entry).filter(
          ([key]) => key !== 'action' && key !== 'route' && key !== 'createdAt',
        ),
      ),
    ),
  });
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : null;
  const message = err ? err.message : String(error);
  enqueueNormalized({
    source: 'frontend',
    category: 'runtime',
    severity: 'error',
    message,
    stack: err?.stack,
    extra: context,
    type: 'js_error',
  });
}

export function captureApiError(
  response: ApiErrorContext,
  context?: Record<string, unknown>,
): void {
  const status = response.status ?? 0;
  const type: ErrorType = status === 0 ? 'network_error' : 'api_error';
  const severity: ErrorSeverity =
    status >= 500 || status === 0 ? 'error' : status >= 400 ? 'warning' : 'info';

  enqueueNormalized({
    source: 'frontend',
    category: status === 0 ? 'network' : 'api',
    severity,
    message: `${response.method ?? 'GET'} ${response.url ?? '(unknown)'} -> ${status || 'network error'}`,
    extra: sanitizeObject({
      ...context,
      apiUrl: response.url,
      apiMethod: response.method,
      statusCode: status,
      requestId: response.requestId,
      durationMs: response.durationMs,
      responseBody: response.body,
    }),
    context: {
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      method: response.method,
      statusCode: status || undefined,
      durationMs: response.durationMs,
      requestId: response.requestId,
    },
    type,
  });
}

export function capturePerformanceMetric(metric: PerformanceMetric): void {
  const severity: ErrorSeverity =
    metric.rating === 'poor' ? 'error' : metric.rating === 'needs-improvement' ? 'warning' : 'info';

  enqueueNormalized({
    source: 'frontend',
    category: 'performance',
    severity,
    message: `${metric.name}: ${metric.value.toFixed(0)} (${metric.rating ?? 'unknown'})`,
    extra: {
      metricName: metric.name,
      metricValue: metric.value,
      metricRating: metric.rating,
    },
    type: 'performance_error',
  });
}

export async function flushErrors(): Promise<void> {
  if (!endpoint || queue.length === 0 || isFlushing) return;
  isFlushing = true;

  const batch = queue.splice(0, Math.min(queue.length, maxBatchSize));
  const body = JSON.stringify({ errors: batch });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`Error reporter failed with ${response.status}`);
    }

    retryCount = 0;
  } catch (error) {
    queue.unshift(...batch);
    retryCount += 1;

    if (retryCount <= MAX_RETRIES) {
      const delay = BASE_BACKOFF_MS * 2 ** (retryCount - 1);
      scheduleFlush(delay);
    } else if (typeof window !== 'undefined') {
      console.warn('[observability] failed to flush errors', error);
    }
  } finally {
    isFlushing = false;
  }
}

export const setUserContext = setUserContextCompat;
